import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { AdminUser, UserModeration } from "@equipped/core/backend/client";
import { isModerationActive } from "@equipped/core/billing/moderation";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";
import { PersonDetailDrawer } from "../components/PersonDetailDrawer";
import { useAdminFeatures } from "../features/AdminFeaturesContext";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

function effectiveAccountStatus(
  moderation: UserModeration | null | undefined,
): "active" | "suspended" | "banned" {
  if (!isModerationActive(moderation, Math.floor(Date.now() / 1000))) return "active";
  return moderation?.status === "banned" ? "banned" : "suspended";
}

function MemberDataCells({
  user,
  canReadGrants,
}: {
  user: AdminUser;
  canReadGrants: boolean;
}) {
  const { client, token } = useAuth();
  const enabled = !!client && !!token;
  const moderationQuery = useQuery({
    queryKey: ["admin-moderation", user.id],
    queryFn: () => client!.adminModeration(token!, user.id),
    enabled,
  });
  const grantsQuery = useQuery({
    queryKey: ["admin-grants", user.id],
    queryFn: () => client!.adminGrants(token!, user.id),
    enabled: enabled && canReadGrants && user.role !== "admin",
  });

  const status = effectiveAccountStatus(moderationQuery.data);
  const now = Math.floor(Date.now() / 1000);
  const activeGrants = (grantsQuery.data ?? []).filter(
    (grant) => grant.expiresAt == null || grant.expiresAt > now,
  );
  const expiredGrantCount = (grantsQuery.data?.length ?? 0) - activeGrants.length;

  return (
    <>
      <TableCell>
        {moderationQuery.isLoading ? (
          <CircularProgress
            size={18}
            aria-label={`Loading account status for ${user.email}`}
          />
        ) : moderationQuery.isError ? (
          <Chip size="small" label="Unavailable" />
        ) : (
          <Chip
            size="small"
            label={
              status === "active"
                ? "Active"
                : status === "banned"
                  ? "Banned"
                  : "Suspended"
            }
            color={
              status === "active" ? "success" : status === "banned" ? "error" : "warning"
            }
          />
        )}
      </TableCell>
      <TableCell>
        {user.role === "admin" ? (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label="All content (admin)"
          />
        ) : !canReadGrants ? (
          <Chip size="small" label="Unavailable" />
        ) : grantsQuery.isLoading ? (
          <CircularProgress
            size={18}
            aria-label={`Loading access grants for ${user.email}`}
          />
        ) : grantsQuery.isError ? (
          <Chip size="small" label="Unavailable" />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.25,
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              color={activeGrants.length > 0 ? "primary" : "default"}
              label={
                activeGrants.length === 0
                  ? "No active grants"
                  : `${activeGrants.length} active ${activeGrants.length === 1 ? "grant" : "grants"}`
              }
            />
            {expiredGrantCount > 0 ? (
              <Typography variant="caption" color="text.secondary">
                {expiredGrantCount} expired
              </Typography>
            ) : null}
          </Box>
        )}
      </TableCell>
    </>
  );
}

/**
 * Membership v0 intentionally exposes only data the current account backend owns:
 * paged users, moderation state, and explicit catalog/pack grants. Tiers and billing
 * stay absent until the account/membership service exists.
 */
export function MembershipPage() {
  const { client, token } = useAuth();
  const { hasCapability } = useAdminFeatures();
  const [rawQuery, setRawQuery] = useState("");
  const [query, setQuery] = useState("");
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [detailFor, setDetailFor] = useState<AdminUser | null>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(rawQuery.trim());
      setPageCursors([undefined]);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [rawQuery]);

  const cursor = pageCursors[pageCursors.length - 1];
  const pageNumber = pageCursors.length;
  const usersQuery = useQuery({
    queryKey: ["admin-users-page", query, "", false, PAGE_SIZE, cursor],
    queryFn: () =>
      client!.adminUsersPage(token!, {
        limit: PAGE_SIZE,
        cursor,
        q: query || undefined,
      }),
    enabled: !!client && !!token,
  });
  const users = usersQuery.data?.users ?? [];
  const nextCursor = usersQuery.data?.nextCursor ?? null;
  const canReadGrants = hasCapability("grants.manage");

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Membership tiers aren&apos;t enabled yet—every account is free for now. Tiers are
        coming with the account/membership service.
      </Alert>

      <MainCard title="Members & access">
        <TextField
          size="small"
          placeholder="Search by name or email…"
          value={rawQuery}
          onChange={(event) => setRawQuery(event.target.value)}
          sx={{ width: "100%", maxWidth: 320, mb: 2 }}
        />

        {usersQuery.isError ? (
          <Alert severity="error">{(usersQuery.error as Error).message}</Alert>
        ) : usersQuery.isLoading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : users.length === 0 ? (
          <Typography color="text.secondary" sx={{ mb: 0 }}>
            {query ? "No members match your search." : "No members yet."}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Account status</TableCell>
                  <TableCell>Access grants</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setDetailFor(user)}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          textTransform: "none",
                          justifyContent: "flex-start",
                        }}
                      >
                        {user.email}
                      </Button>
                    </TableCell>
                    <MemberDataCells user={user} canReadGrants={canReadGrants} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!usersQuery.isLoading &&
        !usersQuery.isError &&
        (users.length > 0 || pageNumber > 1) ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              mt: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Page {pageNumber} · {users.length}{" "}
              {users.length === 1 ? "member" : "members"}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={pageNumber === 1 || usersQuery.isFetching}
              onClick={() => setPageCursors((current) => current.slice(0, -1))}
            >
              Previous
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!nextCursor || usersQuery.isFetching}
              onClick={() => {
                if (nextCursor) setPageCursors((current) => [...current, nextCursor]);
              }}
            >
              Next
            </Button>
          </Box>
        ) : null}
      </MainCard>

      <PersonDetailDrawer
        open={!!detailFor}
        user={detailFor}
        onClose={() => setDetailFor(null)}
      />
    </>
  );
}
